import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  CircularProgress,
  Alert,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
  Divider,
} from '@mui/material';
import type { UserProfile, ChannelNotificationPreferences } from '@finvari/deliver';
import { updateUserProfile, sendTestNotification } from '../firebase';

interface NotificationsDialogProps {
  open: boolean;
  onClose: () => void;
  userProfile: UserProfile;
  // When false (the default), the GitHub channel is hidden — gated by the
  // githubCommentNotificationsEnabled feature flag.
  githubEnabled?: boolean;
}

// The four notifiable scenarios, shown in parallel for each channel.
const SCENARIOS: { key: keyof ChannelNotificationPreferences; label: string }[] = [
  { key: 'deploySuccess', label: 'Deploy succeeded' },
  { key: 'deployFailure', label: 'Deploy failed' },
  { key: 'testSuccess', label: 'Tests passed' },
  { key: 'testFailure', label: 'Tests failed' },
];

// Read a channel's prefs into a fully-populated boolean map. Opt-in: absent is off.
function toState(prefs?: ChannelNotificationPreferences): Record<string, boolean> {
  return Object.fromEntries(SCENARIOS.map((s) => [s.key, prefs?.[s.key] === true]));
}

const NotificationsDialog: React.FC<NotificationsDialogProps> = ({ open, onClose, userProfile, githubEnabled = false }) => {
  const [slack, setSlack] = useState(toState(userProfile.notificationPreferences?.slack));
  const [github, setGithub] = useState(toState(userProfile.notificationPreferences?.github));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (open) {
      setSlack(toState(userProfile.notificationPreferences?.slack));
      setGithub(toState(userProfile.notificationPreferences?.github));
      setError(null);
      setTestResult(null);
    }
  }, [open, userProfile]);

  const handleSendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await sendTestNotification();
      setTestResult({ severity: 'success', message: 'Test notification sent — check your Slack DMs.' });
    } catch (err: any) {
      console.error('Error sending test notification:', err);
      setTestResult({ severity: 'error', message: err.message || 'Failed to send test notification' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only persist GitHub prefs when the channel is exposed, so a hidden section
      // never rewrites (or clears) the user's stored GitHub preferences.
      await updateUserProfile({
        notificationPreferences: {
          slack: slack as ChannelNotificationPreferences,
          ...(githubEnabled ? { github: github as ChannelNotificationPreferences } : {}),
        },
      });
      onClose();
    } catch (err: any) {
      console.error('Error updating notification preferences:', err);
      setError(err.message || 'Failed to update notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const channelSection = (
    title: string,
    state: Record<string, boolean>,
    setState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  ) => (
    <FormGroup>
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
      {SCENARIOS.map((s) => (
        <FormControlLabel
          key={s.key}
          control={
            <Switch
              checked={state[s.key]}
              inputProps={{ 'aria-label': `${title}: ${s.label}` }}
              onChange={(e) => setState((prev) => ({ ...prev, [s.key]: e.target.checked }))}
            />
          }
          label={s.label}
        />
      ))}
    </FormGroup>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Notification Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {channelSection('Slack', slack, setSlack)}
          <Button onClick={handleSendTest} disabled={testing} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
            {testing ? <CircularProgress size={20} /> : 'Send Test Notification'}
          </Button>
          {testResult && <Alert severity={testResult.severity}>{testResult.message}</Alert>}
          {githubEnabled && (
            <>
              <Divider />
              {channelSection('GitHub PR Comments', github, setGithub)}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationsDialog;
